import type { GridColDef } from '@mui/x-data-grid-pro';
import { MoreVert } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyLogo from '@shared/components/CompanyLogo';
import { ModernDataTable } from '@shared/components/DataTable';
import { theme } from '@shared/theme';

interface Review {
  id: number;
  companyId?: number;
  companyName: string;
  companyUrl?: string;
  dealType: string;
  dealSize: string;
  dealArea: string;
  dealClose: string;
  createdAt: string;
  likeDislikeCount?: number;
  likePercentage?: number;
  viewed?: boolean;
  userId?: number;
}

interface ProfileUser {
  id: number;
  firstName?: string;
  lastName?: string;
  username?: string;
  email: string;
  company?: string;
  positionTitle?: string;
  tagLine?: string;
  avatarColor?: string;
}

interface ReviewsTableProps {
  reviews: Review[];
  profileUser: ProfileUser | null;
  loading: boolean;
  isOwnProfile: boolean;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, reviewId: number) => void;
}

function getAuthorInitials(user: ProfileUser | null) {
  if (user?.username) {
    const words = user.username.match(/[A-Z][a-z]+/g) || [];
    if (words.length >= 2) {
      return (words[0]?.[0] || '') + (words[1]?.[0] || '');
    }
    return user.username.substring(0, 2).toUpperCase();
  }
  return (user?.firstName?.[0] || '') + (user?.lastName?.[0] || '');
}

function getAuthorName(user: ProfileUser | null) {
  return user?.username || `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
}

export const ReviewsTable: React.FC<ReviewsTableProps> = ({
  reviews,
  profileUser,
  loading,
  isOwnProfile,
  onMenuOpen,
}) => {
  const navigate = useNavigate();

  const columns: GridColDef[] = [
    {
      field: 'avatar',
      headerName: 'By',
      width: 80,
      display: 'flex',
      sortable: false,
      filterable: false,
      renderCell: () => (
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: profileUser?.avatarColor || '#0b74da',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: '0.875rem',
          }}
        >
          {getAuthorInitials(profileUser)}
        </Box>
      ),
    },
    {
      field: 'author',
      headerName: 'Author',
      flex: 1,
      display: 'flex',
      minWidth: 150,
      valueGetter: () => getAuthorName(profileUser),
    },
    {
      field: 'companyName',
      headerName: 'Company',
      flex: 1.5,
      display: 'flex',
      minWidth: 180,
      renderCell: (params) => {
        const row = params.row as Review;
        return (
          <Box
            onClick={() => navigate(`/company/${encodeURIComponent(row.companyName)}`)}
            sx={{
              'display': 'flex',
              'alignItems': 'center',
              'gap': 1.5,
              'cursor': 'pointer',
              'transition': 'all 0.2s ease',
              'borderRadius': 1.5,
              'p': 0.5,
              'mx': -0.5,
              '&:hover': {
                backgroundColor: 'rgba(120, 187, 241, 0.14)',
                transform: 'translateX(2px)',
              },
            }}
          >
            <CompanyLogo
              companyId={row.companyId}
              companyName={row.companyName}
              companyUrl={row.companyUrl}
              sx={{ width: 28, height: 28 }}
              alt={row.companyName}
            />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'dealType',
      headerName: 'Deal Type',
      flex: 1,
      display: 'flex',
      minWidth: 150,
    },
    {
      field: 'dealSize',
      headerName: 'Deal Size',
      flex: 1,
      display: 'flex',
      minWidth: 130,
    },
    {
      field: 'dealArea',
      headerName: 'Sold To',
      flex: 1,
      display: 'flex',
      minWidth: 150,
    },
    {
      field: 'outcome',
      headerName: 'Outcome',
      flex: 1,
      display: 'flex',
      minWidth: 130,
      renderCell: (params) => {
        const outcome = params.value;
        const color = outcome === 'Deal Won' ? theme.palette.success.main : theme.palette.grey[700];
        return (
          <Box
            component="span"
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.8125rem',
              fontWeight: 600,
              backgroundColor:
                outcome === 'Deal Won'
                  ? 'rgba(76, 175, 80, 0.1)'
                  : 'rgba(147, 147, 147, 0.1)',
              color,
            }}
          >
            {outcome}
          </Box>
        );
      },
    },
    {
      field: 'reactions',
      headerName: 'Reactions',
      flex: 1,
      display: 'flex',
      minWidth: 140,
      renderCell: (params) => {
        const row = params.row as Review;
        if (row.likeDislikeCount && row.likePercentage !== undefined) {
          const likes = Math.round(row.likeDislikeCount * row.likePercentage);
          const dislikes = row.likeDislikeCount - likes;
          return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  border: '1px solid rgba(76, 175, 80, 0.2)',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'success.dark',
                    fontSize: '0.875rem',
                  }}
                >
                  👍
                  {' '}
                  {likes}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                  border: '1px solid rgba(244, 67, 54, 0.2)',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'error.dark',
                    fontSize: '0.875rem',
                  }}
                >
                  👎
                  {' '}
                  {dislikes}
                </Typography>
              </Box>
            </Box>
          );
        }
        return (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.8125rem',
              fontStyle: 'italic',
            }}
          >
            No reactions
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <IconButton
          onClick={e => onMenuOpen(e, params.row.id)}
          size="small"
          sx={{
            'color': 'text.secondary',
            'transition': 'all 0.2s ease',
            '&:hover': {
              color: '#0b74da',
              backgroundColor: 'rgba(11, 116, 218, 0.08)',
              transform: 'scale(1.1)',
            },
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <ModernDataTable
      title="Reviews"
      subtitle={`${reviews.length} ${reviews.length === 1 ? 'review' : 'reviews'} published${profileUser?.tagLine ? ` • ${profileUser.tagLine}` : ''}`}
      rows={reviews.map(review => ({
        id: review.id,
        companyId: review.companyId,
        companyName: review.companyName,
        companyUrl: review.companyUrl,
        dealType: review.dealType,
        dealSize: review.dealSize,
        dealArea: review.dealArea,
        outcome: review.dealClose === 'Yes' ? 'Deal Won' : 'Deal Lost',
        likeDislikeCount: review.likeDislikeCount,
        likePercentage: review.likePercentage,
        viewed: review.viewed,
        userId: review.userId,
      }))}
      columns={columns}
      initialPageSize={10}
      pageSizeOptions={[5, 10, 25, 50, 100]}
      loading={loading}
      hideFooter
    />
  );
};
